// SensorAlarmList — integrated, read-only list of a cell's active sensor errors (CA2, D5).
// Dense table degrading gracefully on missing data: code is always shown (monospaced,
// brand green); description falls back to an em dash when absent or equal to the code
// (CE3); the onset time is shown only when a valid date is present.
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { cellErrorDescription } from '../generic/utilities/cellErrorDescription'
import SensorLevelIcon from './SensorLevelIcon'
import { getDisplayDescription } from './sensorAlarmUtils'

const EM_DASH = '—'

function formatTime (date) {
  if (!date) return ''
  const parsed = new Date(date)
  if (isNaN(parsed.getTime())) return ''
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * Props:
 *   errors — this cell's active sensor errors (all levels, including INF).
 *
 * Returns null when there is nothing to list.
 */
export default function SensorAlarmList ({ errors }) {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()

  if (!errors || errors.length === 0) return null

  return (
    <Box
      data-testid='sensor-alarm-list'
      sx={{
        bgcolor: theme.palette.background.color_212121,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        maxHeight: 220,
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      <Typography
        variant='subtitle1'
        sx={{ display: 'block', px: 1.5, pt: 1.5, pb: 1, color: 'primary.main', fontWeight: 'bold' }}
      >
        {t_('Active alarms')}
      </Typography>
      <Table size='small' aria-label={t_('Active alarms')} sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell padding='checkbox' sx={{ width: 40 }} />
            <TableCell sx={{ color: 'text.secondary', width: 140 }}>{t_('Code')}</TableCell>
            <TableCell sx={{ color: 'text.secondary' }}>{t_('Description')}</TableCell>
            <TableCell sx={{ width: 64 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {errors.map((error, index) => {
            // Story 162, CA13: the cell's PLC errors show their description in the user's language.
            const description = getDisplayDescription({ ...error, description: cellErrorDescription(t_, error) })
            const time = formatTime(error.date)
            return (
              <TableRow key={`${error.code}-${index}`} hover>
                <TableCell padding='checkbox' sx={{ pl: 1 }}>
                  <SensorLevelIcon level={error.level} />
                </TableCell>
                <TableCell
                  sx={{ color: 'primary.main', fontWeight: 'bold', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {error.code}
                </TableCell>
                <TableCell sx={{ color: description ? 'text.primary' : 'text.disabled', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                  {description || EM_DASH}
                </TableCell>
                <TableCell align='right' sx={{ color: 'text.secondary', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {time}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}
